<template>
    <div class="relative pl-2 z-50">
        <button class="cursor-pointer p-0"
            :class="[(showControls ? 'text-white hover:text-indigo-300 bg-gray-900 hover:bg-gray-900 rounded-tl-lg rounded-bl-lg rounded-tr-none rounded-br-none' : 'bg-transparent hover:bg-transparent text-gray-500 hover:text-indigo-400'), (state.enableAnnotator ? 'annotator-on text-yellow-400' : '')]"
            @click="showControls = ! showControls"
            aria-label="Polish Settings"
            :aria-expanded="showControls ? 'true' : 'false'"
        >
            <div class="text-3xl material-icons pl-2 pr-4 py-2 select-none">
                <i class="pr-0 block mx-auto md:inline-block w-6 lg:w-10 fill-current">
                    <svg width="100%" height="100%" viewBox="0 0 22 23" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
                      <g transform="matrix(1,0,0,1,-434.252,-521.095)">
                          <g transform="matrix(0.0871887,0,0,0.0871887,399.459,502.494)">
                              <g transform="matrix(0.449558,0.449558,0.270258,-0.270258,-298.625,-168.563)">
                                  <path d="M1422.26,328.605L1522.19,328.605L1522.19,316.978C1504.66,304.202 1490.73,275.924 1491.82,229.56L1451.98,229.56C1452.41,281.808 1436.52,303.916 1422.26,316.547L1422.26,328.605Z"/>
                              </g>
                              <g transform="matrix(1,0,0,1,120.249,0)">
                                  <path d="M418.747,267.176C360.751,319.912 360.456,316.809 308.104,374.094C325.645,391.96 343.184,409.828 360.72,427.69C419.006,373.532 416.431,374.578 468.765,317.195L418.747,267.176Z"/>
                              </g>
                              <g transform="matrix(1,0,0,1,63.3984,56.6458)">
                                  <path d="M538.083,161.068L499.664,199.48C496.191,202.952 496.192,208.634 499.662,212.105L523.826,236.268C527.301,239.743 532.979,239.739 536.451,236.267L574.871,197.856L579.344,202.329C551.381,234.266 560.234,230.166 531.941,261.542L474.951,204.552C506.431,176.263 522.047,168.374 533.718,156.703L538.083,161.068Z"/>
                              </g>
                              <g transform="matrix(0.392865,0.392865,0.435849,-0.435849,-318.106,17.5785)">
                                  <path d="M1430.93,833.974L1512.76,833.974L1512.77,743.323C1512.77,735.599 1506.46,729.28 1498.73,729.28L1444.98,729.28C1437.26,729.28 1430.94,735.599 1430.94,743.323L1430.93,833.974Z"/>
                              </g>
                              <g transform="matrix(1,0,0,1,-2.70938,8.5443)">
                                  <path d="M634.879,442.415C537.127,435.952 403.22,442.415 403.22,442.415C403.22,442.415 399.955,457.634 403.22,457.474C524.03,451.578 634.879,457.474 634.879,457.474C634.879,457.474 639.888,442.746 634.879,442.415Z"/>
                              </g>
                              <g transform="matrix(1,0,0,1,313.022,-83.4921)">
                                  <path d="M89.312,525.867C93.416,525.685 101.817,525.329 113.357,524.933L115.583,522.707L103.984,511.108L89.312,525.867Z"/>
                              </g>
                          </g>
                      </g>
                    </svg>
                    </i>
            </div>
        </button>

        <div v-if="showControls" class="translate-x-full text-left w-56 bg-gray-900 rounded-tr-lg rounded-br-lg shadow-lg absolute top-0 right-0 p-3">
            <div class="flex items-center">
                <toggle-button
                    name="polish-switch"
                    @change="toggleAnnotator"
                    v-model="state.enableAnnotator"
                    :labels="{checked: 'ON', unchecked: 'OFF'}"
                    :color="{checked: '#76d572', unchecked: '#6e6d7e'}"
                    :width="58"
                    :height="25"
                    :margin="1"
                    :speed="100"
                    ref="annotator_enabled"
                >
                    <div class="select-none text-white text-xs font-semibold ml-3"
                        @click="$refs.annotator_enabled.$el.click()"
                    >
                        Use Polish?
                    </div>
                </toggle-button>
            </div>

            <fieldset>
                <legend class="select-none text-white text-xs font-semibold pt-4 pb-1">
                    View Entries from:
                </legend>

                <div class="flex items-center py-1">
                    <toggle-button
                        name="yourself"
                        @change="toggleAuthor"
                        :value="state.showAuthorIds.includes(self_id)"
                        :tag="self_id.toString()"
                        :color="{checked: '#76d572', unchecked: '#6e6d7e'}"
                        :width="26"
                        :height="15"
                        :margin="1"
                        :speed="100"
                        ref="yourself"
                    >
                        <div class="select-none text-white text-xxs ml-2"
                            @click="$refs.yourself.$el.click()"
                        >
                            Yourself
                        </div>
                    </toggle-button>
                </div>

                <div v-for="author in authors">
                    <div v-if="author.id != self_id" class="flex items-center py-1">
                        <toggle-button
                            :name="author.name"
                            @change="toggleAuthor"
                            :value="state.showAuthorIds.includes(author.id)"
                            :tag="author.id.toString()"
                            :color="{checked: '#76d572', unchecked: '#6e6d7e'}"
                            :width="26"
                            :height="15"
                            :margin="1"
                            :speed="100"
                            :ref="'author_' + author.id"
                        >
                            <div class="select-none text-white text-xxs ml-2"
                                @click="clickAuthorToggle(author.id)"
                            >
                                {{ author.name }}
                            </div>
                        </toggle-button>
                    </div>
                </div>
            </fieldset>
        </div>
    </div>
</template>

<script>
import vClickOutside from 'v-click-outside'
import ToggleButton from '../toggle-button.vue';

export default {
    components: {
        ToggleButton,
    },

    data() {
        return {
            showControls: false,
            self_id: null,
            authors: [],
            state: {
                enableAnnotator: true,
                showAuthorIds: [],
            },
        }
    },
    directives: {
        clickOutside: vClickOutside.directive
    },
    methods: {
        onClickOutside (event) {
              this.showControls = !this.showControls
          },
        toggleAnnotator() {
            this.$parent.$emit('updateAnnotationToggle', this.state);
        },
        toggleAuthor({ value, tag }) {
            if (value == true) {
                this.state.showAuthorIds.push(parseInt(tag));
            } else {
                this.state.showAuthorIds.splice(this.state.showAuthorIds.indexOf(parseInt(tag)), 1);
            }

            this.$parent.$emit('updateAnnotationToggle', this.state);
        },
        clickAuthorToggle(id) {
            let ref_id = 'author_' + id;
            this.$refs[ref_id][0].$el.click();
        },
    },

    created() {
        this.$root.$on('fetchedAnnotationAuthors', (authors) => {
            this.self_id = authors.self_id;
            this.authors = authors.authors;
            this.state.showAuthorIds = _.uniq([this.self_id].concat(
                authors.authors.map(author => author.id)
            ));
            this.$parent.$emit('updateAnnotationToggle', this.state);
        });
    },
}
</script>

<style>
.translate-x-full {
    transform: translateX(100%);
}
</style>

